import LearningLibrary from "@/components/feature/panel/LearningLibrary";
import AccountPackages from "@/components/feature/panel/AccountPackages";
import { StudentOverview } from "@/components/feature/mobile/Experience";
import {useAiCoach} from "@/hooks/useAiCoach";
import { useAuth } from "@/hooks/useAuth";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import { useStudentDashboard } from "@/hooks/useStudentDashboard";
import { extractGradeLevel, resolveStudentIdentity } from "@/lib/profile";
import StatCards from "./components/StatCards";
import TodayProgram from "./components/TodayProgram";
import WeeklyChart from "./components/WeeklyChart";
import LiveClassCard from "./components/LiveClassCard";
import ExamSummary from "./components/ExamSummary";
import WeakTopicsList from "./components/WeakTopicsList";
import MotivationSummary from "./components/MotivationSummary";
import AiLearningOrchestratorCard from "./components/AiLearningOrchestratorCard";
import StudentLevelProfileCard from "./components/StudentLevelProfileCard";

export default function StudentDashboard() {
  const { user } = useAuth();
  const { openCoach } = useAiCoach();
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const { data, loading, error, reload } = useStudentDashboard();
  const identity = resolveStudentIdentity(user);

  const tasksCompleted = data.tasks.filter((t) => t.completed).length;
  const firstName = identity.name.trim().split(/\s+/)[0] || identity.name;
  const gradeLevel = extractGradeLevel(identity.grade);

  return (
    <div className="space-y-6">
      <AccountPackages />
      <StudentOverview name={firstName} grade={identity.grade} data={data} loading={loading} onCoach={openCoach} />

      {/* Hata durumu */}
      {error && (
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <i className="ri-error-warning-line text-accent-700 text-xl" aria-hidden="true"></i>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Veriler yüklenemedi</p>
              <p className="text-xs text-foreground-600">{error}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      )}

      <LearningLibrary />
      <StudentLevelProfileCard gradeLevel={gradeLevel} />

      <AiLearningOrchestratorCard data={data} onApplied={reload} />

      {/* Bugünkü program + sağ sütun */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TodayProgram tasks={data.tasks} loading={loading} />
        </div>

        <div className="space-y-6">
          <MotivationSummary />

          <LiveClassCard liveClass={data.liveClass} loading={loading} />

          {/* AI Koç */}
          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-sparkling-2-line text-base" aria-hidden="true"></i>
              </span>
              <h2 className="font-heading font-semibold text-sm text-foreground-950">{name} Asistan</h2>
            </div>
            <p className="mt-3 text-sm text-foreground-800 leading-relaxed">
              Sana özel öneriler almak için sağ alttaki asistanı aç ve derslerinle ilgili aklına takılanları sor.
            </p>
          </div>
        </div>
      </div>

      {/* Alt satır */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <WeeklyChart studyDays={data.studyDays} loading={loading} />
        </div>

        <ExamSummary lastExam={data.lastExam} loading={loading} />
      </div>

      {/* Eksik konular */}
      <WeakTopicsList weakTopics={data.weakTopics} loading={loading} />
    </div>
  );
}
